import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Check, X, ArrowRight, Sparkle } from "@phosphor-icons/react";
import { useI18n } from "../lib/i18n";

/**
 * Interactive Free vs Premium comparator — shown on claim card to boost conversion.
 * Displays benefits side-by-side with a visible gap that unlocks with Premium.
 */
export default function PremiumComparator({ businessName, compact = false }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(!compact);
  const c = t.comparator;

  const rows = [
    { label: c.row_visible, free: true, premium: true },
    { label: c.row_basic, free: true, premium: true },
    { label: c.row_contact, free: true, premium: true },
    { label: c.row_top, free: false, premium: true },
    { label: c.row_photos, free: false, premium: true },
    { label: c.row_desc, free: false, premium: true },
    { label: c.row_clickable, free: false, premium: true },
    { label: c.row_stats, free: false, premium: true },
    { label: c.row_reviews, free: false, premium: true },
    { label: c.row_flyer, free: false, premium: true },
    { label: c.row_report, free: false, premium: true },
    { label: c.row_hours, free: false, premium: true },
    { label: c.row_shares, free: false, premium: true },
  ];

  const delta = rows.filter((r) => !r.free && r.premium).length;

  return (
    <div className="bg-card border border-border rounded-sm overflow-hidden" data-testid="premium-comparator">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-accent/30 transition-colors"
        data-testid="comparator-toggle"
        aria-expanded={open}
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 shrink-0 rounded-sm bg-secondary/15 text-secondary grid place-items-center">
            <Crown size={18} weight="fill" />
          </div>
          <div>
            <div className="font-display text-base font-medium leading-tight">Free vs Premium</div>
            <div className="text-xs text-muted-foreground">{c.unlock.replace("{n}", delta)}</div>
          </div>
        </div>
        <ArrowRight
          size={16}
          className={`text-muted-foreground transition-transform ${open ? "rotate-90" : ""}`}
        />
      </button>

      {open && (
        <div className="border-t border-border">
          <div className="grid grid-cols-[1fr_auto_auto] text-xs">
            <div className="px-5 py-3 border-b border-border bg-accent/20"></div>
            <div className="px-3 py-3 border-b border-l border-border bg-accent/20 text-center font-bold uppercase tracking-widest text-muted-foreground min-w-[72px]">
              {c.free}
            </div>
            <div className="px-3 py-3 border-b border-l border-border bg-primary/10 text-center font-bold uppercase tracking-widest text-primary min-w-[72px] flex items-center justify-center gap-1">
              <Crown size={10} weight="fill" /> {c.premium.slice(0, 5)}.
            </div>

            {rows.map((r, i) => (
              <React.Fragment key={r.label}>
                <div className="px-5 py-2.5 border-b border-border text-foreground" data-testid={`compare-row-${i}`}>
                  {r.label}
                </div>
                <div className="px-3 py-2.5 border-b border-l border-border text-center">
                  {r.free ? <Check size={14} weight="bold" className="text-primary inline" /> : <X size={14} weight="bold" className="text-muted-foreground/40 inline" />}
                </div>
                <div className="px-3 py-2.5 border-b border-l border-border text-center bg-primary/5">
                  {r.premium ? <Check size={14} weight="bold" className="text-primary inline" /> : <X size={14} weight="bold" className="text-muted-foreground inline" />}
                </div>
              </React.Fragment>
            ))}
          </div>

          <div className="px-5 py-4 bg-accent/20 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs text-muted-foreground max-w-xs leading-relaxed">
              {businessName && <><b className="text-foreground">{businessName}</b> — </>}
              {c.visibility_lift}
            </div>
            <Link
              to="/passez-partenaire"
              className="inline-flex items-center gap-2 bg-secondary text-secondary-foreground hover:bg-[#b56847] hover:text-white px-4 py-2.5 text-xs font-semibold rounded-sm transition-colors whitespace-nowrap"
              data-testid="comparator-cta"
            >
              <Sparkle size={12} weight="fill" /> {c.cta} <ArrowRight size={12} />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
